import type { Hours } from '../schemas/branding.js';

/**
 * Discriminated union describing the operating-hours state at a given moment.
 *
 * Consumers:
 *   - dashboard `/branding/hours` "Today's status" preview
 *   - customer-pwa Home pill + temp-closure banner + "View hours" details
 *
 * Pure / deterministic: same inputs → same output. No I/O, no Date.now() —
 * the caller passes `now` so testing is trivial and time-travel is possible.
 *
 * The "until" times are HH:mm strings (matching the schema), not formatted
 * datetimes — i18n / 12h-vs-24h formatting is the caller's job.
 */

export type HoursStatus =
  | {
      readonly kind: 'temp_closed';
      /** Operator-supplied human message (raw string from messageI18n[locale] caller-picked). */
      readonly message: string;
      /** YYYY-MM-DD; when temporary closure ends (inclusive). */
      readonly until: string;
    }
  | {
      readonly kind: 'holiday_closed';
      /** YYYY-MM-DD of today. */
      readonly date: string;
      /** Optional operator label (e.g. "Hari Raya"). */
      readonly label?: string;
    }
  | {
      readonly kind: 'holiday_open';
      readonly date: string;
      readonly label?: string;
      /** HH:mm close time today. */
      readonly until: string;
    }
  | {
      readonly kind: 'closed';
    }
  | {
      readonly kind: 'open';
      /** HH:mm close time today. */
      readonly until: string;
      /** Optional HH:mm open time today (for "opens at 09:00" copy in callers). */
      readonly openedAt?: string;
    }
  | {
      readonly kind: 'open_24h';
    }
  | {
      readonly kind: 'opens_later';
      /** HH:mm — when the outlet opens today. */
      readonly opensAt: string;
      /** HH:mm — when the outlet closes today. */
      readonly closesAt: string;
    };

type DayKey = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';
const DAY_KEYS: readonly DayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * Compute the operating-hours state at `now` against the tenant's `hours` doc.
 *
 * Resolution order (first match wins):
 *   1. temporaryClosure window (`from`–`to` inclusive, YYYY-MM-DD)
 *   2. holidayOverrides entry matching today's date
 *   3. weekly[dayKey] — open / opens-later / closed depending on HH:mm
 */
export function computeHoursStatus(hours: Hours, now: Date): HoursStatus {
  const today = toDateStr(now);
  const nowHm = toHm(now);

  if (
    hours.temporaryClosure &&
    today >= hours.temporaryClosure.from &&
    today <= hours.temporaryClosure.to
  ) {
    return {
      kind: 'temp_closed',
      message: hours.temporaryClosure.messageI18n['en'] ?? 'Temporarily closed',
      until: hours.temporaryClosure.to,
    };
  }

  const holiday = (hours.holidayOverrides ?? []).find((h) => h.date === today);
  if (holiday) {
    if (!holiday.isOpen) {
      return holiday.label
        ? { kind: 'holiday_closed', date: today, label: holiday.label }
        : { kind: 'holiday_closed', date: today };
    }
    return holiday.label
      ? { kind: 'holiday_open', date: today, label: holiday.label, until: holiday.close ?? '23:59' }
      : { kind: 'holiday_open', date: today, until: holiday.close ?? '23:59' };
  }

  const dayKey = DAY_KEYS[now.getDay()] ?? 'mon';
  const day = hours.weekly[dayKey];
  if (!day || !day.isOpen) return { kind: 'closed' };

  const openAt = day.open ?? '00:00';
  const closeAt = day.close ?? '23:59';
  if (openAt === '00:00' && closeAt === '23:59') return { kind: 'open_24h' };

  if (nowHm < openAt) {
    return { kind: 'opens_later', opensAt: openAt, closesAt: closeAt };
  }
  if (nowHm >= closeAt) {
    return { kind: 'closed' };
  }
  return { kind: 'open', until: closeAt, openedAt: openAt };
}

function toDateStr(d: Date): string {
  // toISOString() returns UTC; we want the operator's local date. Use the
  // platform's local timezone (Asia/Kuala_Lumpur in production). Callers
  // can always pass a `now` already adjusted if they need timezone control.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toHm(d: Date): string {
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

/**
 * Walk forward up to 14 days to find the next time the outlet opens after
 * `now`. Returns the date + open time, or null if no opening is configured
 * in that window (e.g., a permanently-closed outlet, or one in a long
 * temporary-closure window).
 *
 * "Next opening" respects all overrides in the same order as
 * `computeHoursStatus`: temporaryClosure → holidayOverrides → weekly.
 *
 * Used by the customer-pwa /book gate to render copy like:
 *   "Closed today. Opens Monday 09:00."
 *   "Re-opens 2026-05-26 at 09:00."
 */
export function findNextOpening(
  hours: Hours,
  now: Date,
): { readonly date: string; readonly time: string; readonly dayKey: DayKey } | null {
  const startMs = now.getTime();
  for (let offset = 0; offset <= 14; offset += 1) {
    const candidate = new Date(startMs + offset * 24 * 60 * 60 * 1000);
    const dateStr = toDateStr(candidate);
    const dayKey = DAY_KEYS[candidate.getDay()] ?? 'mon';

    // Skip temp-closure window entirely
    if (
      hours.temporaryClosure &&
      dateStr >= hours.temporaryClosure.from &&
      dateStr <= hours.temporaryClosure.to
    ) {
      continue;
    }

    const holiday = (hours.holidayOverrides ?? []).find((h) => h.date === dateStr);
    if (holiday) {
      if (!holiday.isOpen) continue;
      const candidateOpen = holiday.open ?? '00:00';
      // If today, only count if we haven't already passed the open time
      if (offset === 0 && toHm(now) >= candidateOpen) {
        // Already past today's holiday open; check whether the window ends
        // before now too — if not, the outlet is currently open and the
        // caller wouldn't have asked. Skip to tomorrow.
        continue;
      }
      return { date: dateStr, time: candidateOpen, dayKey };
    }

    const day = hours.weekly[dayKey];
    if (!day || !day.isOpen) continue;
    const candidateOpen = day.open ?? '00:00';
    if (offset === 0 && toHm(now) >= candidateOpen) {
      // Already past today's open time → check tomorrow.
      continue;
    }
    return { date: dateStr, time: candidateOpen, dayKey };
  }
  return null;
}
